/**
 * Freelancer-mailadressen — een eigen Microsoft 365-account (voornaam@fullframe.nl, Exchange
 * Online Kiosk) voor een freelancer aanmaken, pauzeren/hervatten en later beëindigen (besluiten
 * Joris 28 sep). Gedeeld component: Admin Instellingen › Freelancer-mailadressen én de pagina
 * /freelancers/mailadressen (recht freelancer_mail).
 *
 * Pauzeren: inloggen uit, mailbox wordt een gedeelde mailbox (mail bewaard), de licentie komt vrij.
 * Hervatten: licentie terug, gewone mailbox, nieuw tijdelijk wachtwoord (één keer getoond).
 *
 * De freelancer logt zelf in (Outlook web/app, MFA via de tenant-policy); er wordt niets naar
 * privémail doorgestuurd. Het tijdelijke wachtwoord komt één keer terug van de server en staat
 * alleen in de state van de wachtwoorddialoog; sluiten = weg.
 *
 * Vangnetten: aanmaken heeft een eigen bevestigingsstap in de dialoog; beëindigen, een nieuw
 * wachtwoord en controleren/herstellen gaan via utils/bevestig (niet terug te draaien vanuit de tool).
 * Pauzeren en hervatten gaan ook via bevestig. Controleren/herstellen staat bij 'onbekend', een
 * onafgeronde actie (vastgelopen_actie) of een aanmaak die ouder is dan 5 minuten.
 * Rechten (Node-proxy /Api/freelancer-mail, de kern controleert opnieuw): zien = freelancer_mail
 * view, knoppen = freelancer_mail edit, Beëindigen (en een begonnen beëindiging afronden) alleen
 * met admin_settings edit.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import {
  appBericht, mailtoLink, VARIANT_LABEL, VARIANT_PROJECTLEIDER, VARIANT_STANDAARD, welkomstmail, welkomstVariant,
} from './freelancerWelkom';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { bevestig } from '../../utils/bevestig';
import { useUserRole } from '../../contexts/UserRoleContext';

async function vraag(pad, opties = {}) {
  const r = await authFetch(`${BACKEND_URL}/Api/freelancer-mail${pad}`, {
    ...opties,
    headers: { 'Content-Type': 'application/json', ...(opties.headers || {}) },
    body: opties.body !== undefined ? JSON.stringify(opties.body) : undefined,
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) {
    const fout = new Error(typeof d.detail === 'string' ? d.detail : (d.error || `Fout ${r.status}`));
    fout.status = r.status;
    fout.account = d.account;
    throw fout;
  }
  return d;
}

export const freelancerMailApi = {
  accounts: () => vraag('/accounts'),
  kandidaten: () => vraag('/kandidaten'),
  voorstel: (id) => vraag(`/voorstel?persoon_airtable_id=${encodeURIComponent(id)}`),
  aanmaken: (id) => vraag('/aanmaken', { method: 'POST', body: { persoon_airtable_id: id } }),
  beeindigen: (id) => vraag('/beeindigen', { method: 'POST', body: { id } }),
  pauzeren: (id) => vraag('/pauzeren', { method: 'POST', body: { id } }),
  hervatten: (id) => vraag('/hervatten', { method: 'POST', body: { id } }),
  wachtwoordReset: (id) => vraag('/wachtwoord-reset', { method: 'POST', body: { id } }),
  herstellen: (id) => vraag('/herstellen', { method: 'POST', body: { id } }),
};

const HERSTEL_NA_MS = 5 * 60 * 1000;   // gelijk aan HERSTEL_NA_MINUTEN in de engine

/** Controleren/herstellen bij 'onbekend', een onafgeronde actie (beëindigen/pauzeren/hervatten), of 'aanmaken' ouder dan 5 minuten. */
export const magHerstellen = (a, nu = Date.now()) => {
  if (a.status === 'onbekend') return true;
  if (a.status !== 'beeindigd' && ['beeindigen', 'pauzeren', 'hervatten'].includes(a.vastgelopen_actie)) return true;   // begonnen actie afmaken
  if (a.status !== 'aanmaken' || !a.aangemaakt_op) return false;
  const s = a.aangemaakt_op;
  const t = new Date(/[zZ]|[+-]\d\d:\d\d$/.test(s) ? s : `${s}Z`).getTime();
  return Number.isFinite(t) && nu - t > HERSTEL_NA_MS;
};

const STATUS = {
  actief: { label: 'Actief', color: 'success' },
  gepauzeerd: { label: 'Gepauzeerd', color: 'info' },
  aanmaken: { label: 'Bezig', color: 'default' },
  onbekend: { label: 'Controleren', color: 'warning' },
  beeindigd: { label: 'Beëindigd', color: 'default' },
};

/** Een actief/gepauzeerd account met een voornemen (vastgelopen_actie) is nog 'Bezig'. */
export const statusVan = (a) => (
  ['actief', 'gepauzeerd'].includes(a.status) && a.vastgelopen_actie ? STATUS.aanmaken : (STATUS[a.status] || STATUS.onbekend)
);

const datum = (s) => {
  if (!s) return '';
  const d = new Date(/[zZ]|[+-]\d\d:\d\d$/.test(s) ? s : `${s}Z`);   // server geeft naïef UTC
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
};

const kopieer = async (tekst, wat) => {
  try { await navigator.clipboard.writeText(tekst); toast.success(`${wat} gekopieerd`); } catch { toast.error('Kopiëren geblokkeerd — selecteer de tekst handmatig'); }
};

const knopSx = { textTransform: 'none', fontWeight: 700 };

/** Toont adres + tijdelijk wachtwoord één keer. Sluiten wist het wachtwoord uit de state. */
/**
 * Welkomstmail kopiëren of openen in de mailapp (zonder wachtwoord), naar het privé-adres uit Personen.
 * De variant volgt uit de functie in Personen (Projectleider → projectleider-variant) en is vóór het
 * versturen zichtbaar en om te zetten.
 */
export function WelkomstmailKnoppen({ naam, upn, priveEmail, functie }) {
  const voorgesteld = welkomstVariant(functie);
  const [variant, setVariant] = useState(voorgesteld);
  useEffect(() => { setVariant(voorgesteld); }, [voorgesteld]);
  const ander = variant === VARIANT_PROJECTLEIDER ? VARIANT_STANDAARD : VARIANT_PROJECTLEIDER;
  const mail = welkomstmail({ naam, upn, variant });
  return (
    <Stack spacing={0.5}>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <Typography variant="body2" data-testid="welkomst-variant">Welkomstmail: {VARIANT_LABEL[variant]}</Typography>
        <Button size="small" onClick={() => setVariant(ander)} sx={{ ...knopSx, color: 'text.secondary' }}>
          Gebruik {VARIANT_LABEL[ander]}
        </Button>
      </Stack>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button size="small" variant="outlined" onClick={() => kopieer(`${mail.onderwerp}\n\n${mail.tekst}`, 'Welkomstmail')} sx={knopSx}>
          Kopieer welkomstmail
        </Button>
        <Button size="small" variant="outlined" href={mailtoLink(priveEmail, mail)} sx={knopSx}>
          {priveEmail ? `Open in mail (${priveEmail})` : 'Open in mail'}
        </Button>
      </Stack>
    </Stack>
  );
}

export function WachtwoordDialoog({ gegevens, onClose }) {
  return (
    <Dialog open={!!gegevens} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem' }}>{gegevens?.titel || 'Account aangemaakt'}</DialogTitle>
      {gegevens && (
        <DialogContent>
          {[['Adres', gegevens.upn], ['Tijdelijk wachtwoord', gegevens.wachtwoord]].map(([label, waarde]) => (
            <Box key={label} sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontFamily: 'monospace', fontSize: 15, wordBreak: 'break-all', flex: 1 }}>{waarde}</Typography>
                <Button size="small" onClick={() => kopieer(waarde, label)} sx={knopSx} aria-label={`${label} kopiëren`}>Kopieer</Button>
              </Stack>
            </Box>
          ))}
          <Alert severity="info" sx={{ mt: 1 }}>
            Geef het wachtwoord door via een ander kanaal (bel/app), niet per mail. Bij de eerste keer inloggen kiest de
            freelancer een nieuw wachtwoord en stelt MFA in.
          </Alert>
          <Stack spacing={1} sx={{ mt: 1.5 }}>
            <Button size="small" variant="outlined" sx={{ ...knopSx, alignSelf: 'flex-start' }}
              onClick={() => kopieer(appBericht({ naam: gegevens.naam, upn: gegevens.upn, wachtwoord: gegevens.wachtwoord, nieuw: gegevens.nieuw }), 'App-bericht met wachtwoord')}>
              Kopieer app-bericht met wachtwoord
            </Button>
            {!gegevens.nieuw && <WelkomstmailKnoppen naam={gegevens.naam} upn={gegevens.upn} priveEmail={gegevens.priveEmail} functie={gegevens.functie} />}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Dit wachtwoord wordt nergens bewaard en is na sluiten niet meer op te vragen.
          </Typography>
        </DialogContent>
      )}
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="contained" disableElevation onClick={onClose} sx={knopSx}>Ik heb het genoteerd</Button>
      </DialogActions>
    </Dialog>
  );
}

function AanmaakDialoog({ open, onClose, onAangemaakt }) {
  const [kandidaten, setKandidaten] = useState(null);
  const [keuze, setKeuze] = useState(null);
  const [voorstel, setVoorstel] = useState(null);
  const [fout, setFout] = useState('');
  const [bezig, setBezig] = useState(false);

  useEffect(() => {
    if (!open) return;
    setKeuze(null); setVoorstel(null); setFout(''); setKandidaten(null);
    freelancerMailApi.kandidaten().then((d) => setKandidaten(d.kandidaten || [])).catch((e) => { setFout(e.message); setKandidaten([]); });
  }, [open]);

  useEffect(() => {
    setVoorstel(null); setFout('');
    if (!keuze) return;
    let weg = false;
    freelancerMailApi.voorstel(keuze.persoon_airtable_id)
      .then((d) => { if (!weg) setVoorstel(d); })
      .catch((e) => { if (!weg) setFout(e.message); });
    return () => { weg = true; };
  }, [keuze]);

  const maakAan = async () => {
    setBezig(true); setFout('');
    try {
      const d = await freelancerMailApi.aanmaken(keuze.persoon_airtable_id);
      onAangemaakt(d);
    } catch (e) {
      setFout(e.message);
      if (e.account) onAangemaakt(null);      // gebruiker bestaat half: lijst verversen, fout blijft zichtbaar
    } finally { setBezig(false); }
  };

  return (
    <Dialog open={open} onClose={bezig ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem' }}>Mailadres aanmaken</DialogTitle>
      <DialogContent>
        <Autocomplete
          sx={{ mt: 1 }}
          options={kandidaten || []}
          loading={kandidaten === null}
          value={keuze}
          onChange={(_e, v) => setKeuze(v)}
          getOptionLabel={(o) => o.naam}
          isOptionEqualToValue={(a, b) => a.persoon_airtable_id === b.persoon_airtable_id}
          noOptionsText="Geen actieve freelancers zonder mailadres"
          renderInput={(params) => <TextField {...params} label="Freelancer" size="small" autoFocus />}
        />
        {keuze && !voorstel && !fout && <CircularProgress size={18} sx={{ mt: 2 }} />}
        {voorstel && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">Nieuw adres</Typography>
            <Typography sx={{ fontFamily: 'monospace', fontSize: 15 }}>{voorstel.upn}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Weergavenaam: {voorstel.weergavenaam}. Kost één Exchange Online Kiosk-licentie.
            </Typography>
          </Box>
        )}
        {fout && <Alert severity="error" sx={{ mt: 2 }}>{fout}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={bezig} sx={{ ...knopSx, color: 'text.secondary' }}>Annuleren</Button>
        <Button variant="contained" disableElevation disabled={!voorstel || bezig} onClick={maakAan} sx={knopSx}>
          {bezig ? 'Bezig…' : 'Aanmaken'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function FreelancerMail() {
  const { permissions } = useUserRole();
  const magSchrijven = !!permissions?.freelancer_mail?.edit;
  const magBeeindigen = !!permissions?.admin_settings?.edit;      // definitief: alleen beheerders
  const [data, setData] = useState(null);
  const [fout, setFout] = useState('');
  const [aanmaken, setAanmaken] = useState(false);
  const [wachtwoord, setWachtwoord] = useState(null);
  const [welkom, setWelkom] = useState(null);
  const [bezigId, setBezigId] = useState(null);

  const laad = useCallback(async () => {
    try { setData(await freelancerMailApi.accounts()); setFout(''); } catch (e) { setFout(e.message); }
  }, []);
  useEffect(() => { laad(); }, [laad]);

  const aangemaakt = (d) => {
    laad();
    if (!d) return;
    setAanmaken(false);
    setWachtwoord({ titel: 'Account aangemaakt', upn: d.upn, wachtwoord: d.tijdelijk_wachtwoord,
      naam: d.account?.naam, priveEmail: d.account?.prive_email, functie: d.account?.functie });
  };

  const beeindig = async (a) => {
    const ok = await bevestig({
      titel: `${a.upn} beëindigen?`,
      tekst: `${a.naam} kan dan niet meer inloggen; lopende sessies worden afgebroken en de Kiosk-licentie komt vrij.\n\n`
        + 'De mailbox blijft nog 30 dagen bewaard (terug te halen in het Microsoft 365-beheercentrum), daarna is hij weg.',
      knop: 'Beëindigen', gevaarlijk: true,
    });
    if (!ok) return;
    setBezigId(a.id);
    try { await freelancerMailApi.beeindigen(a.id); toast.success(`${a.upn} is beëindigd`); } catch (e) { toast.error(e.message, { duration: 8000 }); }
    setBezigId(null); laad();
  };

  const nieuwWachtwoord = async (a) => {
    const ok = await bevestig({
      titel: `Nieuw wachtwoord voor ${a.upn}?`,
      tekst: 'Het huidige wachtwoord werkt daarna niet meer. Je krijgt één keer een tijdelijk wachtwoord te zien.',
      knop: 'Nieuw wachtwoord',
    });
    if (!ok) return;
    setBezigId(a.id);
    try {
      const d = await freelancerMailApi.wachtwoordReset(a.id);
      setWachtwoord({ titel: 'Nieuw tijdelijk wachtwoord', upn: d.upn, wachtwoord: d.tijdelijk_wachtwoord, naam: a.naam, nieuw: true });
    } catch (e) { toast.error(e.message, { duration: 8000 }); }
    setBezigId(null);
  };

  const pauzeer = async (a) => {
    const ok = await bevestig({
      titel: `${a.upn} pauzeren?`,
      tekst: `De Kiosk-licentie komt vrij, de mail blijft bewaard en ${a.naam} kan niet meer inloggen; lopende sessies `
        + 'worden afgebroken.\n\nDe mailbox wordt een gedeelde mailbox. Hervatten kan later, met een nieuw tijdelijk wachtwoord.',
      knop: 'Pauzeren',
    });
    if (!ok) return;
    setBezigId(a.id);
    try { await freelancerMailApi.pauzeren(a.id); toast.success(`${a.upn} is gepauzeerd; de mail blijft bewaard`); } catch (e) { toast.error(e.message, { duration: 10000 }); }
    setBezigId(null); laad();
  };

  const toonNieuwWachtwoord = (a, d, titel) => {
    if (d?.tijdelijk_wachtwoord) {
      setWachtwoord({ titel, upn: d.upn || a.upn, wachtwoord: d.tijdelijk_wachtwoord, naam: a.naam, nieuw: true });
    }
  };

  const hervat = async (a) => {
    const ok = await bevestig({
      titel: `${a.upn} hervatten?`,
      tekst: 'Dit kost weer een Kiosk-licentie. De mailbox wordt weer gewoon, inloggen gaat weer aan en je krijgt één keer '
        + 'een nieuw tijdelijk wachtwoord te zien.',
      knop: 'Hervatten',
    });
    if (!ok) return;
    setBezigId(a.id);
    try {
      const d = await freelancerMailApi.hervatten(a.id);
      toonNieuwWachtwoord(a, d, 'Account hervat');
    } catch (e) { toast.error(e.message, { duration: 10000 }); }
    setBezigId(null); laad();
  };

  const herstel = async (a) => {
    // De tekst volgt de actie die vastliep: een begonnen beëindiging wordt afgemaakt, nooit teruggedraaid.
    const actie = a.vastgelopen_actie;
    const ok = await bevestig(actie === 'beeindigen' ? {
      titel: `Beëindiging van ${a.upn} afronden?`,
      tekst: 'De beëindiging is eerder begonnen maar niet afgerond. De tool schakelt het account uit, meldt het overal af '
        + 'en haalt de licentie weg. De mailbox blijft nog 30 dagen bewaard.',
      knop: 'Beëindiging afronden',
      gevaarlijk: true,
    } : actie === 'pauzeren' ? {
      titel: `Pauze van ${a.upn} afronden?`,
      tekst: 'Het pauzeren is eerder begonnen maar niet afgerond. De tool blokkeert het inloggen, zet de mailbox om naar '
        + 'een gedeelde mailbox en haalt pas daarna de licentie weg. De mail blijft bewaard.',
      knop: 'Pauze afronden',
    } : actie === 'hervatten' ? {
      titel: `Hervatten van ${a.upn} afronden?`,
      tekst: 'Het hervatten is eerder begonnen maar niet afgerond. De tool geeft de licentie terug, zet de mailbox weer '
        + 'gewoon en het inloggen aan. Je krijgt één keer een nieuw tijdelijk wachtwoord te zien.',
      knop: 'Hervatten afronden',
    } : {
      titel: `${a.upn} controleren?`,
      tekst: 'De tool zoekt dit adres op in Microsoft 365. Is de gebruiker aantoonbaar door de tool gemaakt, dan '
        + 'rondt hij groep en licentie af. Bestaat er geen gebruiker, dan wordt de registratie opgeruimd.',
      knop: 'Controleren',
    });
    if (!ok) return;
    setBezigId(a.id);
    try {
      const d = await freelancerMailApi.herstellen(a.id);
      toast.success(d.melding || 'Afgerond', { duration: 8000 });
      toonNieuwWachtwoord(a, d, 'Account hervat');
    } catch (e) { toast.error(e.message, { duration: 10000 }); }
    setBezigId(null); laad();
  };

  const ingesteld = data?.ingesteld !== false;
  const lic = data?.licenties;

  return (
    <Box sx={{ maxWidth: 1000 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
        Een eigen Full Frame-mailadres voor een freelancer. De freelancer logt zelf in via Outlook; er gaat niets naar privémail.
        Pauzeer een account als de freelancer even niet voor ons werkt: de licentie komt vrij en de mail blijft bewaard.
      </Typography>

      {data && !ingesteld && (
        <Alert severity="info" sx={{ mb: 2 }}>{data.melding || 'Freelancer-mail is nog niet ingesteld.'}</Alert>
      )}
      {fout && <Alert severity={/nog niet ingesteld/.test(fout) ? 'info' : 'error'} sx={{ mb: 2 }}>{fout}</Alert>}

      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5 }}>
        {magSchrijven && (
          <Button variant="contained" disableElevation disabled={!data || !ingesteld} onClick={() => setAanmaken(true)} sx={knopSx}>
            Mailadres aanmaken
          </Button>
        )}
        {lic && (
          <Typography variant="body2" color="text.secondary">
            Kiosk-licenties: {lic.vrij} vrij van {lic.totaal}
          </Typography>
        )}
        {data?.live_fout && <Typography variant="body2" color="text.secondary">{data.live_fout}</Typography>}
      </Stack>

      {!data && !fout ? <CircularProgress size={20} /> : data && (
        data.accounts.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Nog geen freelancer-mailadressen.</Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Naam</TableCell>
                  <TableCell>Adres</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Aangemaakt</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {data.accounts.map((a) => {
                  const st = statusVan(a);
                  const klaar = a.status === 'beeindigd';
                  const rustig = !a.vastgelopen_actie;           // geen onafgeronde actie
                  const herstelbaar = magHerstellen(a) && (a.vastgelopen_actie !== 'beeindigen' || magBeeindigen);
                  return (
                    <TableRow key={a.id} sx={klaar ? { '& td': { color: 'text.secondary' } } : undefined}>
                      <TableCell>
                        {a.naam}
                        {a.laatste_fout && (
                          <Typography variant="caption" color="warning.dark" sx={{ display: 'block', maxWidth: 380 }}>{a.laatste_fout}</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{a.upn}</TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined" label={st.label} color={st.color} />
                        {a.status === 'gepauzeerd' && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                            {a.gepauzeerd_op ? `sinds ${datum(a.gepauzeerd_op)} · ` : ''}mail bewaard
                          </Typography>
                        )}
                        {a.live && !klaar && !a.live.ingeschakeld && (
                          <Tooltip title="Het account staat uit in Microsoft 365"><Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>uit</Typography></Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{datum(a.aangemaakt_op)}</Typography>
                        <Typography variant="caption" color="text.secondary">{a.aangemaakt_door}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {bezigId === a.id ? <CircularProgress size={16} /> : !klaar && ingesteld && (
                          <>
                            {magSchrijven && herstelbaar && (
                              <Button size="small" onClick={() => herstel(a)} sx={knopSx}
                                aria-label={`${a.upn} controleren en herstellen`}>Controleren/herstellen</Button>
                            )}
                            {magSchrijven && a.status === 'actief' && rustig && (
                              <Button size="small" onClick={() => setWelkom(a)} sx={{ ...knopSx, color: 'text.secondary' }}>Welkomstmail</Button>
                            )}
                            {magSchrijven && a.status === 'actief' && rustig && (
                              <Button size="small" onClick={() => nieuwWachtwoord(a)} sx={{ ...knopSx, color: 'text.secondary' }}>Nieuw wachtwoord</Button>
                            )}
                            {magSchrijven && a.status === 'actief' && rustig && (
                              <Button size="small" onClick={() => pauzeer(a)} sx={knopSx}
                                aria-label={`${a.upn} pauzeren`}>Pauzeren</Button>
                            )}
                            {magSchrijven && a.status === 'gepauzeerd' && rustig && (
                              <Button size="small" onClick={() => hervat(a)} sx={knopSx}
                                aria-label={`${a.upn} hervatten`}>Hervatten</Button>
                            )}
                            {magBeeindigen && a.entra_user_id && a.status !== 'aanmaken' && (
                              <Button size="small" color="error" onClick={() => beeindig(a)} sx={knopSx}
                                aria-label={`${a.upn} beëindigen`}>Beëindigen</Button>
                            )}
                          </>
                        )}
                        {klaar && <Typography variant="caption">{datum(a.beeindigd_op)}</Typography>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )
      )}

      <AanmaakDialoog open={aanmaken} onClose={() => setAanmaken(false)} onAangemaakt={aangemaakt} />
      <WachtwoordDialoog gegevens={wachtwoord} onClose={() => setWachtwoord(null)} />
      <Dialog open={!!welkom} onClose={() => setWelkom(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem' }}>Welkomstmail voor {welkom?.naam}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Uitleg over het adres, wanneer het te gebruiken en hoe in te loggen. Zonder wachtwoord: dat gaat apart via de app.
          </Typography>
          {welkom && <WelkomstmailKnoppen naam={welkom.naam} upn={welkom.upn} priveEmail={welkom.prive_email} functie={welkom.functie} />}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}><Button onClick={() => setWelkom(null)} sx={knopSx}>Sluiten</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
